import { getConnectedBlock, getVisibleBeads } from "./board";
import type {
  GameEffect,
  GameState,
  GameUpdate,
  LevelDefinition,
  Point,
  RejectionReason,
  RuleConfig,
} from "./types";
import { assertValidLevel, resolveRules } from "./validation";

export function createGame(
  level: LevelDefinition,
  overrides: Partial<RuleConfig> = {},
): GameState {
  assertValidLevel(level, overrides);
  const rules = resolveRules(level, overrides);
  return {
    levelId: level.id,
    revision: 0,
    status: "playing",
    failureReason: null,
    elapsedMs: 0,
    remainingMs: level.durationMs,
    rules,
    trayCapacity: rules.trayCapacity,
    removedBeadIds: [],
    selectedBeadIds: [],
    tray: [],
    litTargetSlotIds: [],
  };
}

export function resetGame(
  level: LevelDefinition,
  overrides: Partial<RuleConfig> = {},
): GameState {
  return createGame(level, overrides);
}

function rejected(state: GameState, reason: RejectionReason): GameUpdate {
  return { state, effects: [], accepted: false, reason };
}

export function expandTray(
  level: LevelDefinition,
  state: GameState,
): GameUpdate {
  if (level.id !== state.levelId) {
    return rejected(state, "level-mismatch");
  }
  if (state.status !== "playing") {
    return rejected(state, "game-not-playing");
  }
  if (state.trayCapacity >= state.rules.maxTrayCapacity) {
    return rejected(state, "tray-at-max");
  }

  return {
    accepted: true,
    effects: [{
      type: "tray-expanded",
      fromCapacity: state.trayCapacity,
      toCapacity: state.rules.maxTrayCapacity,
    }],
    state: {
      ...state,
      revision: state.revision + 1,
      trayCapacity: state.rules.maxTrayCapacity,
    },
  };
}

export function selectBlock(
  level: LevelDefinition,
  state: GameState,
  point: Point,
): GameUpdate {
  if (level.id !== state.levelId) {
    return rejected(state, "level-mismatch");
  }
  if (state.status !== "playing") {
    return rejected(state, "game-not-playing");
  }

  const block = getConnectedBlock(level.board, state, point);
  if (block.length === 0) {
    return rejected(state, "empty-cell");
  }

  return {
    accepted: true,
    effects: [],
    state: {
      ...state,
      revision: state.revision + 1,
      selectedBeadIds: block.map((bead) => bead.beadId),
    },
  };
}

export function placeSelectedBlockInTray(
  level: LevelDefinition,
  state: GameState,
  startIndex: number,
): GameUpdate {
  if (level.id !== state.levelId) return rejected(state, "level-mismatch");
  if (state.status !== "playing") return rejected(state, "game-not-playing");
  if (!Number.isInteger(startIndex) || startIndex < 0 || startIndex >= state.trayCapacity) {
    return rejected(state, "invalid-tray-slot");
  }
  if (state.selectedBeadIds.length === 0) return rejected(state, "nothing-selected");

  const selected = new Set(state.selectedBeadIds);
  const visible = getVisibleBeads(level.board, state)
    .filter((bead) => selected.has(bead.beadId));
  if (visible.length !== state.selectedBeadIds.length) return rejected(state, "nothing-selected");

  const endIndex = startIndex + visible.length;
  const occupied = new Set(state.tray.map((bead) => bead.trayIndex));
  if (endIndex > state.trayCapacity || Array.from({ length: visible.length }, (_, order) => occupied.has(startIndex + order)).some(Boolean)) {
    return rejected(state, "tray-space-unavailable");
  }

  const effects: GameEffect[] = visible.map((bead, order) => ({
    type: "collect",
    order,
    beadId: bead.beadId,
    color: bead.color,
    from: { x: bead.x, y: bead.y },
    trayIndex: startIndex + order,
  }));
  const tray = [...state.tray, ...visible.map((bead, order) => ({
    beadId: bead.beadId,
    color: bead.color,
    source: { x: bead.x, y: bead.y },
    trayIndex: startIndex + order,
  }))].sort((left, right) => left.trayIndex - right.trayIndex);

  return {
    accepted: true,
    effects,
    state: {
      ...state,
      revision: state.revision + 1,
      removedBeadIds: [...state.removedBeadIds, ...state.selectedBeadIds],
      selectedBeadIds: [],
      tray,
    },
  };
}

export function advanceTime(
  level: LevelDefinition,
  state: GameState,
  elapsedMs: number,
): GameUpdate {
  if (level.id !== state.levelId) {
    return rejected(state, "level-mismatch");
  }
  if (state.status !== "playing") {
    return rejected(state, "game-not-playing");
  }
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return rejected(state, "invalid-elapsed-time");
  }

  const nextElapsed = Math.min(level.durationMs, state.elapsedMs + elapsedMs);
  const remainingMs = Math.max(0, level.durationMs - nextElapsed);
  const timedOut = remainingMs === 0 && state.rules.failOnTimeout;
  const effects: GameEffect[] = [{ type: "time", elapsedMs: nextElapsed, remainingMs }];
  if (timedOut) {
    effects.push({ type: "game-ended", status: "failed", reason: "timeout" });
  }
  return {
    accepted: true,
    effects,
    state: {
      ...state,
      revision: state.revision + 1,
      status: timedOut ? "failed" : state.status,
      failureReason: timedOut ? "timeout" : state.failureReason,
      elapsedMs: nextElapsed,
      remainingMs,
    },
  };
}
